(() => {
  'use strict';
  if (window.shotproDeliveryLoaded) return;
  window.shotproDeliveryLoaded = true;

  const HANDLE = 'shotpro-livraison-sans-souci';
  const ROOT = window.Shopify?.routes?.root || '/';
  const WIDGET = '[data-shotpro-delivery]';
  const CONSENT = '_shotpro_delivery_consent';

  let cart = null, busy = false, message = '', ready = false;
  let queue = Promise.resolve(), timer, catalogue = null;
  let checkoutBusy = false;
  let pendingChoice = null, variantCache = null;
  let pendingTotal = null;
  let choiceVersion = 0, choiceQueued = false, needsRefresh = false;

  const isService = item => item.handle === HANDLE;
  const lines = basket => basket.items.filter(isService);

  const optedIn = basket =>
    lines(basket).some(item => item.properties?.[CONSENT] === 'v2');

  const subtotal = basket => basket.items.reduce((sum, item) =>
    sum + (!isService(item) && item.requires_shipping
      ? item.final_line_price : 0), 0);

  const fee = amount => amount <= 0 || amount > 500000 ? null :
    amount <= 6400 ? 128 : Math.ceil(amount / 500) * 10;

  const money = amount => new Intl.NumberFormat(
    document.documentElement.lang || 'fr',
    { style: 'currency', currency: 'EUR' }
  ).format(amount / 100);

  async function request(path, body) {
    const response = await fetch(ROOT + path, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...(body ? {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      } : {})
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));

      throw new Error(
        error.description ||
        error.message ||
        'Erreur Shopify : ' + response.status
      );
    }

    return response.json();
  }

  async function data(optionId) {
    const url = new URL(ROOT + 'products/' + HANDLE, location.origin);
    url.searchParams.set('section_id', 'shotpro-delivery-data');

    if (optionId) {
      url.searchParams.set('option_values', optionId);
    }

    const response = await fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error('Option indisponible pour le moment.');
    }

    const html = new DOMParser().parseFromString(
      await response.text(),
      'text/html'
    );

    const node = html.querySelector('[data-shotpro-delivery-data]');

    if (!node) {
      throw new Error('Option indisponible pour le moment.');
    }

    const result = JSON.parse(node.textContent);

    if (
      result.error ||
      result.handle !== HANDLE ||
      result.currency !== 'EUR'
    ) {
      throw new Error('Option indisponible pour le moment.');
    }

    return result;
  }

  async function loadCatalogue() {
    if (catalogue) return catalogue;

    const result = await data();

    if (
      !Array.isArray(result.names) ||
      result.names.length !== 989 ||
      result.optionIds?.length !== result.names.length
    ) {
      throw new Error(
        'Les tarifs de livraison ne sont pas tous disponibles.'
      );
    }

    const prices = new Map();

    result.names.forEach((name, index) => {
      const cents = Math.round(
        Number(
          String(name)
            .replace(/[^0-9,.]/g, '')
            .replace(',', '.')
        ) * 100
      );

      if (
        !Number.isInteger(cents) ||
        !result.optionIds[index] ||
        prices.has(cents)
      ) {
        throw new Error('Les tarifs de livraison sont invalides.');
      }

      prices.set(cents, String(result.optionIds[index]));
    });

    if (!prices.has(128)) {
      throw new Error('Le tarif minimum est absent.');
    }

    for (let cents = 130; cents <= 10000; cents += 10) {
      if (!prices.has(cents)) {
        throw new Error('Un tarif de livraison est absent.');
      }
    }

    catalogue = { productId: result.productId, prices };
    return catalogue;
  }

  function resolveVariant(cents) {
    if (
      variantCache?.cents === cents &&
      Date.now() < variantCache.expires
    ) {
      return variantCache.promise;
    }

    const entry = {
      cents,
      expires: Date.now() + 30000
    };

    entry.promise = fetchVariant(cents).catch(error => {
      if (variantCache === entry) variantCache = null;
      throw error;
    });

    variantCache = entry;
    return entry.promise;
  }

  function preloadVariant() {
    if (!cart || !ready || busy || cart.currency !== 'EUR') return;

    const drawerOpen = document.querySelector('cart-drawer[open]');
    const cartPage = window.themeVariables?.settings?.pageType === 'cart';

    if (!drawerOpen && !cartPage) return;

    const cents = fee(subtotal(cart));

    if (cents !== null) {
      resolveVariant(cents).catch(() => {});
    }
  }

  async function fetchVariant(cents) {
    const list = await loadCatalogue();
    const result = await data(list.prices.get(cents));
    const variant = result.selected;
    const sku = 'SP-LSS-' + String(cents).padStart(5, '0');

    if (
      result.productId !== list.productId ||
      !variant?.id ||
      !variant.available ||
      variant.requiresShipping !== false ||
      variant.price !== cents ||
      variant.sku !== sku
    ) {
      throw new Error('Le tarif demandé est indisponible.');
    }

    return variant;
  }

  function updateDisplayedTotal(amount) {
    if (!Number.isSafeInteger(amount) || amount < 0) return;

    document.querySelectorAll('[data-shotpro-cart-total]').forEach(node => {
      node.textContent = money(amount) + ' EUR';
    });
  }

  function render() {
    if (pendingTotal !== null) {
      updateDisplayedTotal(pendingTotal);
    }

    const cents = cart && cart.currency === 'EUR'
      ? fee(subtotal(cart)) : null;

    document.querySelectorAll(WIDGET).forEach(widget => {
      widget.hidden = !!cart &&
        subtotal(cart) === 0 &&
        lines(cart).length === 0;

      const toggle = widget.querySelector(
        '[data-shotpro-delivery-toggle]'
      );

      if (toggle) {
        toggle.checked = pendingChoice !== null
          ? pendingChoice
          : !!cart && optedIn(cart);

        toggle.disabled = !cart ||
          (!toggle.checked && (!ready || cents === null));
      }

      const price = widget.querySelector(
        '[data-shotpro-delivery-price]'
      );

      if (price) {
        price.textContent = cents === null ? '' : money(cents);
      }

      const status = widget.querySelector(
        '[data-shotpro-delivery-message]'
      );

      if (status) {
        status.textContent = message || (
          cart && cents === null && subtotal(cart) > 0
            ? 'Option indisponible pour ce panier.'
            : ''
        );

        status.hidden = !status.textContent;
      }

      widget.setAttribute('aria-busy', String(busy));
    });
  }

  function refreshTheme() {
    document.dispatchEvent(new CustomEvent('cart:refresh', {
      detail: { shotproDelivery: true }
    }));

    if (window.themeVariables?.settings?.pageType === 'cart') {
      location.reload();
    }
  }

  async function removeService(basket) {
    const updates = Object.fromEntries(
      lines(basket).map(item => [item.key, 0])
    );

    if (Object.keys(updates).length) {
      await request('cart/update.js', { updates });
    }
  }

  async function sync(choice, version) {
    busy = true;
    message = '';
    render();

    let changed = false;

    try {
      cart = await request('cart.js');

      const wanted = typeof choice === 'boolean'
        ? choice
        : optedIn(cart);

      const cents = cart.currency === 'EUR'
        ? fee(subtotal(cart))
        : null;

      if (!wanted || cents === null) {
        if (lines(cart).length) {
          changed = true;
          await removeService(cart);
          cart = await request('cart.js');

          if (lines(cart).length) {
            throw new Error(
              'Le retrait du service a échoué. Réessaie.'
            );
          }
        }

        if (cents !== null) {
          try {
            await loadCatalogue();
            ready = true;
          } catch (error) {
            ready = false;
            message = error.message;
          }
        }

        return true;
      }

      const variant = await resolveVariant(cents);
      ready = true;

      const current = lines(cart);

      if (
        current.length !== 1 ||
        current[0].variant_id !== variant.id ||
        current[0].quantity !== 1 ||
        current[0].properties?.[CONSENT] !== 'v2'
      ) {
        changed = true;
        await removeService(cart);

        await request('cart/add.js', {
          items: [{
            id: variant.id,
            quantity: 1,
            properties: { [CONSENT]: 'v2' }
          }]
        });
      }

      cart = await request('cart.js');
      const final = lines(cart);

      if (
        fee(subtotal(cart)) !== cents ||
        final.length !== 1 ||
        final[0].variant_id !== variant.id ||
        final[0].quantity !== 1 ||
        final[0].final_line_price !== cents ||
        !optedIn(cart)
      ) {
        changed = true;
        await removeService(cart);
        cart = await request('cart.js');

        throw new Error(
          'Le tarif a changé ou une réduction affecte le service. ' +
          'L’option a été retirée ; réessaie.'
        );
      }

      return true;
    } catch (error) {
      variantCache = null;
      message = error.message ||
        'Option indisponible pour le moment.';

      try {
        cart = await request('cart.js');
      } catch (_) {
        // Conserver le message d’erreur.
      }

      return false;
    } finally {
      busy = false;

      if (
        typeof choice === 'boolean' &&
        version === choiceVersion
      ) {
        pendingChoice = null;
        pendingTotal = null;

        if (cart?.currency === 'EUR') {
          updateDisplayedTotal(cart.total_price);
        }
      }

      render();

      if (!message) preloadVariant();

      needsRefresh = needsRefresh || changed;

      if (needsRefresh && pendingChoice === null) {
        needsRefresh = false;
        refreshTheme();
      }
    }
  }

  function enqueue(choice) {
    if (typeof choice === 'boolean') {
      pendingChoice = choice;

      if (
        cart?.currency === 'EUR' &&
        Number.isSafeInteger(cart.total_price)
      ) {
        const currentFee = lines(cart).reduce(
          (sum, item) => sum + item.final_line_price,
          0
        );

        const nextFee = choice ? fee(subtotal(cart)) : 0;

        pendingTotal = nextFee === null
          ? null
          : cart.total_price - currentFee + nextFee;
      }

      busy = true;
      render();
      choiceVersion += 1;

      if (!choiceQueued) {
        choiceQueued = true;

        queue = queue.then(async () => {
          try {
            let processedVersion;

            do {
              processedVersion = choiceVersion;
              await sync(pendingChoice, processedVersion);
            } while (processedVersion !== choiceVersion);
          } finally {
            choiceQueued = false;
          }
        });
      }

      return queue;
    }

    queue = queue.then(() => sync());
    return queue;
  }

  function schedule(event) {
    if (event?.detail?.shotproDelivery) return;

    clearTimeout(timer);
    timer = setTimeout(() => enqueue(), 1400);
  }

  document.addEventListener('change', event => {
    if (!event.target.matches('[data-shotpro-delivery-toggle]')) {
      return;
    }

    clearTimeout(timer);
    enqueue(event.target.checked);
  });

  document.addEventListener('cart:change', schedule);
  document.addEventListener('cart:refresh', schedule);

  window.addEventListener('pageshow', event => {
    if (event.persisted) schedule();
  });

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) schedule();
  });

  document.addEventListener('click', async event => {
    const button = event.target.closest('[name="checkout"]');

    if (!button || !document.querySelector(WIDGET)) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (checkoutBusy) return;

    checkoutBusy = true;
    clearTimeout(timer);

    let ok = await enqueue();

    while (pendingChoice !== null || choiceQueued) {
      await queue;
      ok = await enqueue();
    }

    checkoutBusy = false;

    if (ok) location.assign(ROOT + 'checkout');
  }, true);

  new MutationObserver(records => {
    const widgetAdded = records.some(record =>
      [...record.addedNodes].some(node =>
        node.nodeType === 1 &&
        (node.matches(WIDGET) || node.querySelector(WIDGET))
      )
    );

    if (widgetAdded) {
      render();
      preloadVariant();
    }

    const drawerChanged = records.some(record =>
      record.type === 'attributes' &&
      record.target.matches('cart-drawer')
    );

    if (drawerChanged) preloadVariant();
  }).observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['open']
  });

  enqueue();
})();